import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GridColDef } from "@mui/x-data-grid";
import { Box, Chip, Typography } from "@mui/material";
import { Construction } from "@mui/icons-material";
import CustomDataGrid from "../../../../components/ui/customDataGrid/customDataGrid.component";
import RelevantDateCell from "../../../../components/service/relevantDateCell/relevantDateCell.component";
import GridAddressCell from "../../../../components/ui/gridAddressCell/gridAddressCell.component";
import {
  JOBS_ENDPOINT,
  JOB_STATUS_COLOR_MAP,
  JOB_STATUS_OPTIONS,
  getJobKindChipSx,
} from "../../../../consts";
import { useHasAnyPermission, useTenantName } from "../../../../contexts/auth";
import {
  containsStringFilterOperators,
  singleSelectFilterOperators,
} from "../../../../components/ui/customDataGrid/filterOperators";

/** The jobs handed over to one external service, newest first. */
export const ExternalServiceJobs = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const tenantName = useTenantName();
  const canViewJobs = useHasAnyPermission(["service.view_job", "service.view_own_job"]);

  const statusOptions = useMemo(
    () => JOB_STATUS_OPTIONS.map((option) => ({ value: option.value, label: t(option.labelKey) })),
    [t]
  );
  const extraParams = useMemo(() => ({ external_service: id }), [id]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "number",
        headerName: t("Number"),
        minWidth: 140,
        filterOperators: containsStringFilterOperators(),
        renderCell: (params) => {
          const kindChipSx = getJobKindChipSx(params.row.kind);
          return kindChipSx ? (
            <Chip label={params.value || "-"} size="small" sx={kindChipSx} />
          ) : (
            <Typography component="span" variant="body2">
              {params.value || "-"}
            </Typography>
          );
        },
      },
      {
        field: "status",
        headerName: t("Status"),
        type: "singleSelect",
        valueOptions: statusOptions,
        filterOperators: singleSelectFilterOperators(),
        renderCell: (params) => (
          <Chip
            label={params.row.status_display || params.value || "-"}
            color={JOB_STATUS_COLOR_MAP[params.row.status] ?? "default"}
            size="small"
          />
        ),
      },
      {
        field: "timeline_date",
        headerName: t("Relevant date"),
        sortable: false,
        filterable: false,
        renderCell: (params) => <RelevantDateCell value={params.value} status={params.row.status} />,
      },
      {
        field: "short_name",
        headerName: t("Client"),
        sortable: false,
        filterOperators: containsStringFilterOperators(),
        valueGetter: (_value, row) => row.short_name || "-",
      },
      {
        field: "equipment_names",
        headerName: t("Equipments"),
        sortable: false,
        filterable: false,
        flex: 1,
        minWidth: 200,
        renderCell: (params) => {
          const names: string[] = Array.isArray(params.row.equipment_names) ? params.row.equipment_names : [];
          const addresses: string[] = Array.isArray(params.row.equipment_addresses) ? params.row.equipment_addresses : [];
          if (names.length === 0) return "-";
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, py: 0.5 }}>
              {names.map((name, index) => (
                <Box key={`${name}-${index}`}>
                  <Typography variant="body2" sx={{ lineHeight: 1.15 }}>
                    {name || "-"}
                  </Typography>
                  <GridAddressCell addressText={addresses[index] || ""} fallbackText={tenantName} singleLine />
                </Box>
              ))}
            </Box>
          );
        },
      },
      {
        // Technicians of the workspace can be on the job next to the service.
        field: "assigned_to_names",
        headerName: t("Technicians"),
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) =>
          Array.isArray(row.assigned_to_names) && row.assigned_to_names.length > 0
            ? row.assigned_to_names.join(", ")
            : "-",
      },
    ],
    [t, statusOptions, tenantName]
  );

  if (!canViewJobs || !id) {
    return null;
  }

  return (
    <CustomDataGrid
      icon={<Construction />}
      title={t("Assigned jobs")}
      url={JOBS_ENDPOINT}
      gridSettingsKey="EXTERNAL_SERVICE_JOBS"
      extraParams={extraParams}
      columns={columns}
      dataGridProps={{ getRowHeight: () => "auto" }}
      refreshEventName={["jobAdded", "externalServiceUpdated"]}
      onRowClick={(jobId) => navigate(`/service/jobs/${jobId}`)}
    />
  );
};
